import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { userRequestService } from "../../../../services/userRequestService";

import {
  StyledTable as Table,
  StyledTableHead as THead,
  StyledTableRow as TR,
  StyledTableHeader as TH,
  StyledTableCell as TD,
  HoverTR,
  Container,
  ActionButton,
  ButtonContainer,
  DesktopOnly,
  MobileCards,
  MobileCard,
  MobileCardTitle,
  MobileCardMeta,
  MobileCardActions,
} from "./styles";

const USER_TYPE_LABELS = {
  GUARDIAN: 'Guardian',
  PROP: 'Proprietário',
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Não informado';
  return value;
};

const UserResquestTable = ({ filteredUsers, onUserUpdate }) => {
  const navigate = useNavigate();

  const safeFilteredUsers = () => {
    if (!filteredUsers) return [];

    if (filteredUsers.data && Array.isArray(filteredUsers.data)) {
      return filteredUsers.data;
    }

    if (Array.isArray(filteredUsers)) {
      return filteredUsers;
    }

    return [];
  };

  const userData = safeFilteredUsers();

  const handleRejectUser = async (user) => {
    const { value: rejectionReason } = await Swal.fire({
      title: 'Deseja rejeitar esse usuário?',
      text: `Usuário: ${user.full_name}`,
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Motivo da rejeição',
      inputPlaceholder: 'Digite o motivo da rejeição...',
      inputAttributes: {
        'aria-label': 'Digite o motivo da rejeição'
      },
      showCancelButton: true,
      confirmButtonColor: 'var(--danger)',
      cancelButtonColor: 'var(--info)',
      confirmButtonText: 'Sim, rejeitar!',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value) {
          return 'Por favor, digite o motivo da rejeição!';
        }
      }
    });

    if (rejectionReason) {
      try {
        Swal.fire({
          title: 'Processando...',
          text: 'Rejeitando usuário...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await userRequestService.rejectUser(user.id, rejectionReason);

        Swal.fire(
          'Rejeitado!',
          'O usuário foi rejeitado com sucesso.',
          'success'
        );

        if (onUserUpdate) {
          onUserUpdate();
        }

      } catch (error) {
        console.error('Erro ao rejeitar usuário:', error);
        Swal.fire(
          'Erro!',
          'Não foi possível rejeitar o usuário. Tente novamente.',
          'error'
        );
      }
    }
  };

  const handleApproveUser = (user) => {
    if (user.status && user.status !== 'PENDENTE' && user.status !== 'ANALISE') {
      Swal.fire({
        title: 'Solicitação já processada',
        text: `Status atual: ${user.status}`,
        icon: 'info',
        background: 'var(--surface-overlay)',
        color: 'var(--accent-secondary)',
        confirmButtonColor: 'var(--accent)'
      });
      return;
    }

    Swal.fire({
      title: 'Deseja aprovar esse usuário?',
      text: `Usuário: ${user.full_name}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: 'var(--success)',
      cancelButtonColor: 'var(--text-muted)',
      confirmButtonText: 'Sim, aprovar!',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/add_user_request', { state: { requestUser: user } });
      }
    });
  };

  if (userData.length === 0) {
    return (
      <Container>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Nenhuma solicitação de usuário encontrada
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <DesktopOnly>
        <Table>
          <THead>
            <TR>
              <TH>Nome</TH>
              <TH>E-mail</TH>
              <TH>Telefone</TH>
              <TH>Localidade</TH>
              <TH>CEP</TH>
              <TH>Tipo</TH>
              <TH>Status</TH>
              <TH>Ação</TH>
            </TR>
          </THead>
          <tbody>
            {userData.map((row, index) => (
              <HoverTR key={row.id || index} className="hover">
                <TD>{displayValue(row.full_name)}</TD>
                <TD>{displayValue(row.email)}</TD>
                <TD>{displayValue(row.phone)}</TD>
                <TD>
                  {displayValue(row.city)}
                  {row.state ? `, ${row.state}` : ''}
                </TD>
                <TD>{displayValue(row.cep)}</TD>
                <TD>{USER_TYPE_LABELS[row.user_type] || displayValue(row.user_type)}</TD>
                <TD>{displayValue(row.status)}</TD>
                <TD>
                  <ButtonContainer>
                    <ActionButton
                      approve
                      onClick={() => handleApproveUser(row)}
                      title="Aprovar usuário"
                      disabled={row.status === 'APROVADO' || row.status === 'ACEITO' || row.status === 'RECUSADO'}
                    >
                      ✓
                    </ActionButton>
                    <ActionButton
                      reject
                      onClick={() => handleRejectUser(row)}
                      title="Rejeitar usuário"
                      disabled={row.status === 'APROVADO' || row.status === 'ACEITO' || row.status === 'RECUSADO'}
                    >
                      ✕
                    </ActionButton>
                  </ButtonContainer>
                </TD>
              </HoverTR>
            ))}
          </tbody>
        </Table>
      </DesktopOnly>

      <MobileCards>
        {userData.map((row, index) => (
          <MobileCard key={`m-${row.id || index}`}>
            <MobileCardTitle>{displayValue(row.full_name)}</MobileCardTitle>
            <MobileCardMeta>{displayValue(row.email)}</MobileCardMeta>
            <MobileCardMeta>{displayValue(row.phone)}</MobileCardMeta>
            <MobileCardMeta>
              {displayValue(row.city)}
              {row.state ? `, ${row.state}` : ''}
            </MobileCardMeta>
            <MobileCardMeta>CEP: {displayValue(row.cep)}</MobileCardMeta>
            <MobileCardMeta>
              Tipo: {USER_TYPE_LABELS[row.user_type] || displayValue(row.user_type)}
            </MobileCardMeta>
            <MobileCardMeta>Status: {displayValue(row.status)}</MobileCardMeta>
            <MobileCardActions>
              <ButtonContainer>
                <ActionButton
                  approve
                  onClick={() => handleApproveUser(row)}
                  title="Aprovar usuário"
                  disabled={row.status === 'APROVADO' || row.status === 'ACEITO' || row.status === 'RECUSADO'}
                >
                  ✓ Aprovar
                </ActionButton>
                <ActionButton
                  reject
                  onClick={() => handleRejectUser(row)}
                  title="Rejeitar usuário"
                  disabled={row.status === 'APROVADO' || row.status === 'ACEITO' || row.status === 'RECUSADO'}
                >
                  ✕ Rejeitar
                </ActionButton>
              </ButtonContainer>
            </MobileCardActions>
          </MobileCard>
        ))}
      </MobileCards>
    </Container>
  );
};

export default UserResquestTable;
